import { useState, useEffect, useRef } from 'react'
import { Upload, AlertCircle, CheckCircle, X, Edit3, Save } from 'lucide-react'
import { apiClient } from '../services/api'
import { getReceiptImageCompressionSettings } from '../services/receiptImageCompressionConfig'
import { ui } from '../styles/ui'
import PageShell from '../components/ui/PageShell'
import { useAuthStore } from '../services/authStore'
import { formatCurrency } from '../utils/currency'
import { formatDateTime } from '../utils/date'
import { useTranslation } from '../contexts/TranslationContext'
import { useCurrencyCatalog } from '../contexts/CurrencyCatalogContext'
import SearchableCurrencySelect from '../components/ui/SearchableCurrencySelect'
import { prepareReceiptImageUpload } from '../utils/receiptImageCompression'

export default function ReceiptUpload() {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState(null)
  const formatFileSize = (size) => `${Math.max(1, Math.round(size / 1024)).toLocaleString()} KB`
  const [error, setError] = useState(null)
  const [categories, setCategories] = useState([])
  const [savingReceipt, setSavingReceipt] = useState(false)
  const [receiptSaved, setReceiptSaved] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { user } = useAuthStore()
  const { t } = useTranslation()
  const { currencies: CURRENCY_LIST } = useCurrencyCatalog()
  const [formData, setFormData] = useState({
    merchant: '',
    amount: '',
    currency: user?.default_currency || 'USD',
    exchange_rate: '',
    date: '',
    category_id: '',
    notes: '',
    payment_method: 'cash'
  })

  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)

  useEffect(() => {
    loadCategories()
  }, [])

  useEffect(() => {
    const loadReceipt = async (receiptId) => {
      try {
        const response = await apiClient.get(`/receipts/${receiptId}`)
        setResult((prev) => ({
          ...prev,
          receipt: response.data,
        }))
      } catch (err) {
        console.error('Failed to load receipt status:', err)
      }
    }

    if (!result?.receipt?.id) return

    const status = result.receipt.ocr_status
    if (status === 'pending' || status === 'processing') {
      const interval = setInterval(() => {
        loadReceipt(result.receipt.id)
      }, 3000)
      return () => clearInterval(interval)
    }

    if (result.receipt.ocr_status === 'completed' && result.receipt.ocr_data) {
      setFormData({
        merchant: result.receipt.ocr_data.merchant || '',
        amount: normalizeAmount(result.receipt.ocr_data.amount || ''),
        currency: result.receipt.ocr_data.currency || user?.default_currency || 'USD',
        exchange_rate: result.receipt.ocr_data.exchange_rate || '',
        date: toDateTimeLocal(result.receipt.ocr_data.date) || toDateTimeLocal(new Date()),
        category_id: result.receipt.ocr_data.category_id ? String(result.receipt.ocr_data.category_id) : '',
        notes: '',
        payment_method: 'cash'
      })
    }
  }, [result])

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/categories', { params: { type: 'product' } })
      setCategories((response.data?.data ?? response.data) || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setError(null)
      setResult(null)
      setReceiptSaved(false)

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => setPreview(e.target.result)
      reader.readAsDataURL(selectedFile)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    dropZoneRef.current?.classList.add('border-blue-500', 'bg-blue-50')
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    dropZoneRef.current?.classList.remove('border-blue-500', 'bg-blue-50')
  }

  const handleDrop = (e) => {
    e.preventDefault()
    dropZoneRef.current?.classList.remove('border-blue-500', 'bg-blue-50')

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile)
      setError(null)
      setResult(null)
      setReceiptSaved(false)

      const reader = new FileReader()
      reader.onload = (e) => setPreview(e.target.result)
      reader.readAsDataURL(droppedFile)
    } else {
      setError(t('Please drop a valid image file', 'Please drop a valid image file'))
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    if (!file) {
      setError(t('Please select a file', 'Please select a file'))
      return
    }

    try {
      setUploading(true)
      setError(null)

      const compressionSettings = await getReceiptImageCompressionSettings()
      const compressed = await prepareReceiptImageUpload(file, compressionSettings)
      const payload = {
        source: 'image',
        sourceData: { base64: compressed.dataUrl, mimeType: compressed.mimeType },
      }

      if (formData.category_id) {
        payload.category_id = formData.category_id
      }
      if (formData.notes?.trim()) {
        payload.notes = formData.notes.trim()
      }

      const response = await apiClient.post('/receipts/upload', payload)

      setResult({
        receipt: response.data.receipt,
        message: response.data.message,
      })
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('Upload failed', 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveReceipt = async () => {
    if (!formData.merchant || !formData.amount || !formData.date) {
      setError(t('Please fill in all required fields', 'Please fill in all required fields'))
      return
    }

    if (!result?.receipt?.id) {
      setError(t('No receipt available to update. Please upload a receipt first.', 'No receipt available to update. Please upload a receipt first.'))
      return
    }

    try {
      setSavingReceipt(true)
      setError(null)

      const receiptData = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: formData.category_id || null,
      }

      await apiClient.put(`/receipts/${result.receipt.id}`, receiptData)

      setReceiptSaved(true)
      setResult(null)
      setFile(null)
      setPreview(null)
      setFormData({
        merchant: '',
        amount: '',
        currency: user?.default_currency || 'USD',
        exchange_rate: '',
        date: '',
        category_id: '',
        notes: '',
        payment_method: 'cash'
      })
    } catch (err) {
      setError(err.response?.data?.message || t('Failed to save receipt', 'Failed to save receipt'))
    } finally {
      setSavingReceipt(false)
    }
  }

  const renderReceiptForm = () => {
    if (!result?.receipt?.ocr_status || result.receipt.ocr_status !== 'completed' || !result.receipt.ocr_data) {
      return null
    }

    return (
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Edit3 size={20} />
          {t('Review & Save Receipt', 'Review & Save Receipt')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`${ui.label} mb-1`}>{t('Merchant *', 'Merchant *')}</label>
            <input
              type="text"
              value={formData.merchant}
              onChange={(e) => handleFormChange('merchant', e.target.value)}
              className={ui.input}
              placeholder={t('Store or restaurant name', 'Store or restaurant name')}
            />
          </div>

          <div>
            <label className={`${ui.label} mb-1`}>{t('Amount *', 'Amount *')}</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => handleFormChange('amount', e.target.value)}
              className={ui.input}
              placeholder="0.00"
            />
          </div>

          <div>
            <label className={`${ui.label} mb-1`}>{t('Currency *', 'Currency *')}</label>
            <SearchableCurrencySelect
              id="receipt-upload-currency"
              value={formData.currency}
              options={CURRENCY_LIST}
              onChange={(value) => handleFormChange('currency', value)}
              emptyLabel={t('Select currency', 'Select currency')}
              searchPlaceholder={t('Search currency', 'Search currency')}
              searchAriaLabel={t('Search currency', 'Search currency')}
            />
          </div>

          <div>
            <label className={`${ui.label} mb-1`}>{t('Exchange Rate (to base)', 'Exchange Rate (to base)')}</label>
            <input
              type="number"
              step="0.000001"
              value={formData.exchange_rate}
              onChange={(e) => handleFormChange('exchange_rate', e.target.value)}
              className={ui.input}
              placeholder="1.000000"
            />
          </div>

          <div>
            <label className={`${ui.label} mb-1`}>{t('Date & Time *', 'Date & Time *')}</label>
            <input
              type="datetime-local"
              value={formData.date}
              onChange={(e) => handleFormChange('date', e.target.value)}
              className={ui.input}
            />
          </div>

          <div>
            <label className={`${ui.label} mb-1`}>{t('Category', 'Category')}</label>
            <p className={ui.subtitle}>{t('Choose a receipt category only.', 'Choose a receipt category only.')}</p>
            <select
              value={formData.category_id}
              onChange={(e) => handleFormChange('category_id', e.target.value)}
              className={ui.select}
            >
              <option value="">{t('Select category', 'Select category')}</option>
              {categories.map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.icon ? `${category.icon} ` : ''}{category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`${ui.label} mb-1`}>{t('Payment Method', 'Payment Method')}</label>
            <select
              value={formData.payment_method}
              onChange={(e) => handleFormChange('payment_method', e.target.value)}
              className={ui.select}
            >
              <option value="cash">{t('Cash', 'Cash')}</option>
              <option value="card">{t('Card', 'Card')}</option>
              <option value="digital">{t('Digital Wallet', 'Digital Wallet')}</option>
              <option value="other">{t('Other', 'Other')}</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="receipt-notes" className={`${ui.label} mb-1`}>{t('Notes', 'Notes')}</label>
            <textarea
              id="receipt-notes"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              className={ui.input}
              rows="3"
              placeholder={t('Additional notes about this receipt', 'Additional notes about this receipt')}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-2">
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSaveReceipt}
            disabled={savingReceipt}
            className={`flex-1 ${ui.buttonSuccess}`}
          >
            {savingReceipt ? (
              <>
                <div className={ui.spinner}></div>
                {t('Saving...', 'Saving...')}
              </>
            ) : (
              <>
                <Save size={20} />
                {t('Save Receipt', 'Save Receipt')}
              </>
            )}
          </button>

          <button
            onClick={() => setResult(null)}
            className={ui.buttonGhost}
          >
            {t('Edit Data', 'Edit Data')}
          </button>
        </div>
      </div>
    )
  }

  const toDateTimeLocal = (value) => {
    if (value === null || value === undefined || value === '') return ''

    const parseValue = (input) => {
      if (!input) return null

      if (input instanceof Date) {
        return input
      }

      const raw = String(input).trim()
      const dotMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s*u\s*)?(\d{1,2}:\d{2}(?::\d{2})?)?$/i)
      if (dotMatch) {
        const [, day, month, year, time] = dotMatch
        const [hour = '0', minute = '0', second = '0'] = (time ?? '').split(':')
        return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
      }

      const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/)
      if (isoMatch) {
        const [, year, month, day, hour = '0', minute = '0', second = '0'] = isoMatch
        return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
      }

      const parsed = new Date(raw)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }

    const date = parseValue(value)
    if (!date) return ''

    const pad = (num) => String(num).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === '') return ''
    const numeric = String(value).trim().replace(/[^0-9.-]/g, '')
    return numeric
  }

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    const num = Number(value)
    if (Number.isNaN(num)) return String(value)
    return num.toFixed(2)
  }

  const resetUpload = async () => {
    try {
      if (result?.receipt?.id && !receiptSaved) {
        await apiClient.delete(`/receipts/${result.receipt.id}`)
      }
    } catch (err) {
      console.warn('Failed to delete unconfirmed receipt', err)
    } finally {
      setFile(null)
      setPreview(null)
      setResult(null)
      setError(null)
      setReceiptSaved(false)
      setFormData({
        merchant: '',
        amount: '',
        currency: user?.default_currency || 'USD',
        exchange_rate: '',
        date: '',
        category_id: '',
        notes: '',
        payment_method: 'cash'
      })
    }
  }

  return (
    <PageShell>
      <div className="flex items-center justify-between">
        <h1 className={ui.title}>{t('Upload Receipt', 'Upload Receipt')}</h1>
        {file && (
          <button
            onClick={resetUpload}
            className={ui.buttonGhost}
          >
            <X size={20} />
            {t('Reset', 'Reset')}
          </button>
        )}
      </div>

        {/* Success Message */}
        {receiptSaved && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-2">
            <CheckCircle size={20} className="text-green-600" />
            <div>
              <p className="text-green-800 font-semibold">{t('Receipt saved successfully!', 'Receipt saved successfully!')}</p>
              <p className="text-green-600 text-sm">{t('You can upload another receipt or continue reviewing receipts.', 'You can upload another receipt or continue reviewing receipts.')}</p>
            </div>
          </div>
        )}

        {/* Upload Form */}
        {!result && (
          <form onSubmit={handleUpload} className="bg-surface p-6 rounded-lg shadow space-y-4">
            <div
              ref={dropZoneRef}
              className="border-2 border-dashed border-outline rounded-lg p-8 text-center transition-colors"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload size={48} className="mx-auto text-muted-foreground mb-4" />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="receipt-input"
              />
              <label htmlFor="receipt-input" className="cursor-pointer">
                <p className="text-muted-foreground mb-2">
                  {t('Drop receipt here or', 'Drop receipt here or')} <span className="text-blue-600 underline">{t('click to browse', 'click to browse')}</span>
                </p>
                <p className="text-sm text-muted-foreground">{t('Supports JPG, PNG, and other image formats', 'Supports JPG, PNG, and other image formats')}</p>
              </label>

              {file && (
                <div className="mt-4">
                  <p className="text-blue-600 font-semibold">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-1">{t('Category', 'Category')}</label>
                <p className="text-xs text-muted-foreground mb-1">{t('Optional receipt category for this upload.', 'Optional receipt category for this upload.')}</p>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={(e) => handleFormChange('category_id', e.target.value)}
                  className="w-full border border-outline rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('Optional: select a receipt category', 'Optional: select a receipt category')}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={String(category.id)}>
                      {category.icon ? `${category.icon} ` : ''}{category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="upload-notes" className="block text-sm font-medium text-foreground mb-1">{t('Notes', 'Notes')}</label>
                <p className="text-xs text-muted-foreground mb-1">{t('Optional note saved with this receipt.', 'Optional note saved with this receipt.')}</p>
                <textarea
                  id="upload-notes"
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  rows="2"
                  className="w-full border border-outline rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('e.g. Weekly grocery run', 'e.g. Weekly grocery run')}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-2">
                <AlertCircle size={20} className="text-red-600" />
                <p className="text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={!file || uploading}
              className={`w-full ${ui.buttonPrimary} py-3`}
            >
              {uploading ? (
                <>
                  <div className={ui.spinner}></div>
                  Queueing Receipt...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Queue Receipt
                </>
              )}
            </button>
          </form>
        )}

        {/* Image Preview */}
        {preview && !result && (
          <div className="bg-surface p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">{t('Receipt Preview', 'Receipt Preview')}</h2>
            <div className="flex justify-center overflow-hidden">
              <img
                src={preview}
                alt="Receipt preview"
                onClick={() => setIsModalOpen(true)}
                className="max-w-full max-h-96 rounded-lg shadow transition-transform duration-300 ease-in-out hover:scale-105 cursor-zoom-in"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-3">{t('Click image to open fullscreen preview.', 'Click image to open fullscreen preview.')}</p>
          </div>
        )}

        {preview && !result && isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setIsModalOpen(false)}>
            <div className="relative max-h-[90vh] max-w-[90vw]">
              <img
                src={preview}
                alt="Receipt fullscreen"
                className="h-full w-full object-contain rounded-lg shadow-xl"
              />
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="absolute top-2 right-2 text-white bg-black/50 p-2 rounded-full hover:bg-black"
              >
                Г—
              </button>
            </div>
          </div>
        )}

        {/* OCR Result & Receipt Form */}
        {result && (
          <div className="bg-surface p-6 rounded-lg shadow space-y-6">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" />
              <h2 className="text-xl font-semibold">{t('Receipt Queued for Analysis', 'Receipt Queued for Analysis')}</h2>
            </div>
            <div className="text-sm text-muted-foreground">
              <p>{result.message}</p>
              <p className="mt-2">Receipt ID: <strong>{result.receipt?.id}</strong></p>
              <p>Status: <strong>{result.receipt?.ocr_status || 'pending'}</strong></p>
              {result.receipt?.product_analysis_status && (
                <p>Product analysis status: <strong>{result.receipt.product_analysis_status}</strong></p>
              )}
            </div>

            {/* Screenshot miniature with hover zoom */}
            {preview && (
              <div className="mt-3">
                <h3 className="text-sm font-medium text-foreground mb-2">{t('Uploaded Receipt', 'Uploaded Receipt')}</h3>
                <div className="w-28 h-28 overflow-hidden rounded-lg border border-outline">
                  <img
                    src={preview}
                    alt="Receipt miniature"
                    onClick={() => setIsModalOpen(true)}
                    className="w-full h-full object-cover transition-transform duration-300 ease-in-out hover:scale-150 cursor-zoom-in"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('Hover to zoom. Click to open fullscreen.', 'Hover to zoom. Click to open fullscreen.')}</p>
              </div>
            )}

            {isModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setIsModalOpen(false)}>
                <div className="relative max-h-[90vh] max-w-[90vw]">
                  <img
                    src={preview}
                    alt="Receipt fullscreen"
                    className="h-full w-full object-contain rounded-lg shadow-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="absolute top-2 right-2 text-white bg-black/50 p-2 rounded-full hover:bg-black"
                  >
                    Г—
                  </button>
                </div>
              </div>
            )}

            {result.receipt?.ocr_status === 'completed' && result.receipt.ocr_data && (
              <div className="bg-surface-elevated p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">{t('Extracted Data (editable)', 'Extracted Data (editable)')}</h3>
                  <button
                    type="button"
                    onClick={() => {
                      const extracted = result.receipt.ocr_data
                      if (!extracted) return
                      setFormData({
                        merchant: extracted.merchant || '',
                        amount: normalizeAmount(extracted.amount || ''),
                        currency: extracted.currency || user?.default_currency || 'USD',
                        exchange_rate: extracted.exchange_rate || '',
                        date: extracted.date || new Date().toISOString().split('T')[0],
                        category_id: '',
                        notes: extracted.raw_text || '',
                        payment_method: 'cash',
                      })
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {t('Apply extracted values', 'Apply extracted values')}
                  </button>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>{t('Merchant:', 'Merchant:')}</strong> {result.receipt.ocr_data.merchant || t('Not detected', 'Not detected')}</p>
                  <p><strong>{t('Amount:', 'Amount:')}</strong> {result.receipt.ocr_data.amount ? formatCurrency(normalizeAmount(result.receipt.ocr_data.amount), result.receipt.ocr_data.currency || user?.default_currency || 'USD') : t('Not detected', 'Not detected')}</p>
                  <p><strong>{t('Date:', 'Date:')}</strong> {result.receipt.ocr_data.date ? formatDateTime(result.receipt.ocr_data.date) : t('Not detected', 'Not detected')}</p>
                  <p><strong>Subtotal:</strong> {result.receipt.ocr_data.subtotal ?? 'N/A'}</p>
                  <p><strong>Tax:</strong> {result.receipt.ocr_data.tax ?? 'N/A'}</p>
                  {result.receipt.ocr_data.items && result.receipt.ocr_data.items.length > 0 && (
                    <div>
                      <strong>Items:</strong>
                      <div className="mt-2 overflow-x-auto border rounded-lg border-outline">
                        <table className="min-w-full text-sm">
                          <thead className="bg-surface-elevated">
                            <tr>
                              <th className="px-3 py-2 text-left">{t('Description', 'Description')}</th>
                              <th className="px-3 py-2 text-right">{t('Quantity', 'Quantity')}</th>
                              <th className="px-3 py-2 text-right">{t('Unit Price', 'Unit Price')}</th>
                              <th className="px-3 py-2 text-right">{t('Discount', 'Discount')}</th>
                              <th className="px-3 py-2 text-right">{t('Line Total', 'Line Total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.receipt.ocr_data.items.map((item, index) => {
                              const itemQuantity = Number(item.quantity ?? 1);
                              const itemUnitPrice = Number(item.price ?? item.unit_price ?? 0);
                              const itemDiscountRaw = item.discount ?? item.discount_amount ?? null;
                              let itemDiscount = null;

                              if (itemDiscountRaw !== null && itemDiscountRaw !== undefined && itemDiscountRaw !== '') {
                                const discountValue = String(itemDiscountRaw).trim();
                                if (discountValue.endsWith('%')) {
                                  const percent = parseFloat(discountValue.replace('%', '').trim());
                                  if (!Number.isNaN(percent)) {
                                    itemDiscount = Math.abs(percent) * itemUnitPrice * itemQuantity / 100;
                                  }
                                } else {
                                  const parsed = parseFloat(discountValue.replace(/[^0-9.-]+/g, ''));
                                  if (!Number.isNaN(parsed)) {
                                    itemDiscount = Math.abs(parsed);
                                  }
                                }
                              }

                              const rawLineTotal = item.total_price ?? item.total ?? (itemUnitPrice * itemQuantity);
                              let lineTotal = Number(rawLineTotal);
                              if (Number.isNaN(lineTotal)) {
                                lineTotal = itemUnitPrice * itemQuantity;
                              }

                              if (itemDiscount !== null && lineTotal === itemUnitPrice * itemQuantity) {
                                lineTotal = itemUnitPrice * itemQuantity - itemDiscount;
                              }

                              return (
                                <tr key={index} className="border-t border-outline">
                                  <td className="px-3 py-2">{item.description || item.name || 'Unknown'}</td>
                                  <td className="px-3 py-2 text-right">{item.quantity ?? 1}</td>
                                  <td className="px-3 py-2 text-right">{item.price ?? item.unit_price ?? '-'}</td>
                                  <td className="px-3 py-2 text-right">{item.discount ?? item.discount_amount ?? '-'}</td>
                                  <td className="px-3 py-2 text-right">{formatMoney(lineTotal)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {renderReceiptForm()}
          </div>
        )}
    </PageShell>
    )
  }

